import BlogCommentSection from '@c/BlogCommentSection';
import { NextSeo } from "next-seo";
import { addCacheTag } from '@vercel/functions';
import { ChevronLeftIcon, ChevronRightIcon, ShareAltIcon } from '@c/icons';
import { DefaultLayout } from '@c/layouts';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import { BlogDocument } from '@util/types/firestore/blog';
import { GetServerSideProps } from 'next';
import Link from 'next/link';
import { ParsedUrlQuery } from 'querystring';
import {
  getBlogBySlug,
  parseFaqFromHtml,
  type FaqPair
} from '@util/blog';
import { BlogPostingSchema, FAQSchema } from '@c/StructuredData';
import { resolveDateModified } from '@util/blog/blogDates';
import { AuthorInfo } from '@c/AuthorInfo';
import { resolveBlogAuthor } from '@util/blog/author';
import { useAuth } from 'context/AuthContext';
import { useState } from 'react';
import ShareModal from '@c/modals/ShareModal';
import { isMobile } from '@util/index';
import { useQuery } from '@tanstack/react-query';

import { viewBlogPost, clickBlogAd } from '@util/analytics';
import { AdType, getNAds } from '@util/firestore/ads';

export interface HeadMetaTag {
  attributes?: Record<string, string>;
  content?: string;
  property?: string;
  key: string;
}


const PUBLISHER_NAME = 'GearFocus';
const PUBLISHER_LOGO = 'https://gearfocus.com/logo.svg';
const DEFAULT_OG_IMAGE = 'https://gearfocus.com/gear_pile.png';

/**
 * Normalize a blog date into ISO 8601 for schema/OpenGraph.
 *
 * blog.created is typed as a number but the WordPress mapping actually passes a WP
 * date string, so we accept either. Numeric (or numeric-string) values are treated
 * as millisecond epochs. Anything unparseable returns undefined so callers omit the
 * field entirely rather than emit garbage.
 */
function normalizeBlogDate(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;

  let date: Date;
  if (typeof value === 'number') {
    date = new Date(value);
  } else if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    date = /^\d+$/.test(trimmed) ? new Date(Number(trimmed)) : new Date(trimmed);
  } else {
    return undefined;
  }

  return isNaN(date.getTime()) ? undefined : date.toISOString();
}

type BlogPaginationLink = {
  title: string;
  slug: string;
  thumbnail: string;
};

interface BlogDetailPageProps {
  blog: BlogDocument;
  nextLink?: BlogPaginationLink;
  prevLink?: BlogPaginationLink;
  ads: AdType[]
  datePublished: string | null;
  dateModified: string | null;
  faqs: FaqPair[];
}

interface Params extends ParsedUrlQuery {
  slug: string[];
}

const BlogDetailPage = ({ blog, nextLink, prevLink, ads, datePublished, dateModified, faqs }: BlogDetailPageProps) => {
  const [openModal, setOpenModal] = useState(false);
  const { userDoc } = useAuth();
  const author = resolveBlogAuthor(blog.author);

  useQuery(
    ['blog-view', blog?.id],
    () => viewBlogPost(blog, userDoc?.uid),
    {
      enabled: !!blog?.id && blog.is_published,
    }
  );

  return (
    <>
      <NextSeo
        title={blog.meta_tags.title}
        description={blog.meta_tags.description}
        canonical={`https://gearfocus.com/blog/${blog.slug}`}
        openGraph={{
          images: [
            {
              url: blog.thumbnail || DEFAULT_OG_IMAGE,
              width: 800,
              height: 600,
              alt: blog.meta_tags.title,
              type: "image/jpeg",
            },
          ],
          siteName: "GearFocus Marketplace",
          type: 'article',
          url: `https://gearfocus.com/blog/${blog.slug}`,
          ...(datePublished && {
            article: {
              publishedTime: datePublished,
              ...(dateModified && { modifiedTime: dateModified }),
            },
          }),
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      <BlogPostingSchema
        headline={blog.title}
        description={blog.meta_tags.description || blog.description}
        image={blog.full_image || blog.thumbnail || DEFAULT_OG_IMAGE}
        authorName={author.name}
        authorType={author.type}
        publisher={PUBLISHER_NAME}
        publisherLogo={PUBLISHER_LOGO}
        datePublished={datePublished ?? undefined}
        dateModified={dateModified ?? undefined}
        url={`https://gearfocus.com/blog/${blog.slug}`}
      />

      {faqs.length >= 2 && <FAQSchema faqs={faqs} />}

      <div className="flex w-full justify-center gap-[4rem]" itemScope itemType="http://schema.org/Article">
        <div className="mb-[2rem] mt-[6rem] w-full overflow-hidden max-w-[79.2rem] px-4 sm:px-0">
          <h1 className="text-center text-[3.2rem] font-semibold sm:text-left sm:text-[4.4rem]" itemProp="headline">
            {blog.title}
          </h1>
          {/* tags */}
          <div className="mt-4 flex justify-center gap-[.8rem] sm:mt-[2.4rem] sm:justify-start">
            <Link
              href={{ pathname: '/blog' }}
              scroll={false}
            >
              <div
                className="cursor-pointer select-none rounded-[4rem] bg-brand-secondary-light px-4 py-[.7rem] text-[1.3rem] text-brand-secondary transition hover:bg-brand-secondary hover:text-white"
              >
                Blog
              </div>
            </Link>
            {blog.categories?.map((cat) => (
              <Link
                key={cat.slug}
                href={{ pathname: '/blog', query: { category: cat.slug } }}
                scroll={false}
              >
                <div
                  className="cursor-pointer select-none rounded-[4rem] bg-brand-secondary-light px-4 py-[.7rem] text-[1.3rem] text-brand-secondary transition hover:bg-brand-secondary hover:text-white"
                >
                  {cat.name}
                </div>
              </Link>
            ))}
          </div>
          {/* author info */}
          <div className="mt-[4rem]  flex  items-center justify-between gap-[2rem] ">
            <AuthorInfo blog={blog} />

            <Button
              type="text"
              text={isMobile() ? '' : 'Share This Blog'}
              leadingIcon={<ShareAltIcon />}
              noXPadding
              onClick={() => setOpenModal(true)}
            />
            <ShareModal
              isOpen={openModal}
              title={blog.title}
              thumb={blog.thumbnail}
              type="blog"
              dismiss={() => setOpenModal(false)}
            />
          </div>

          <div className="relative mt-[4rem] max-h-[56rem] w-full max-w-[79.2rem] overflow-clip">
            <SafeImage
              className="overflow-clip rounded-[1rem] object-cover"
              alt={'blog image'}
              src={blog.full_image ?? blog.thumbnail}
              width={792}
            />
          </div>

          {/* body */}
          <div
            className="editor-content mt-[2rem] sm:mt-[4rem]"
            itemProp='description'
            dangerouslySetInnerHTML={{ __html: blog.html }}
          />

          {/* CTA */}
          <div className="mt-[4rem] flex w-full flex-col items-center justify-between rounded-[1rem] bg-brand-primary-lighter p-[2.4rem] sm:flex-row">
            <div className="flex items-center gap-[1.6rem]">
              <CashIcon />
              <div>
                <h3 className="text-[1.8rem] font-semibold">
                  Own one like this?
                </h3>
                <p className="mt-[.2rem]">Make room for new gear in minutes.</p>
              </div>
            </div>
            <div className="w-full sm:w-fit">
              <Button
                type="secondary"
                text="Sell Yours & Earn Money"
                href="/sell/become-a-seller"
                width={'fluid'}
              />
            </div>
          </div>

          {/* comment section */}
          <BlogCommentSection blogId={blog.id} />

          {/* pagination */}
          {prevLink && nextLink && (
            <div className="mt-[4rem] flex flex-wrap items-start justify-between">
              <Link
                className="mb-[2.6rem] flex items-center justify-between gap-[1.2rem] font-semibold text-brand-secondary hover:scale-105 hover:text-brand-primary"
                href={prevLink.slug}
                scroll={true}
              >
                <ChevronLeftIcon />
                <SafeImage
                  height={40}
                  width={40}
                  src={prevLink?.thumbnail}
                  alt={'previous blog post image'}
                  className="h-[4rem] w-[4rem] grow rounded-[1rem] object-cover"
                />
                <p className="max-w-[20rem]">{prevLink?.title}</p>
              </Link>
              <Link
                className="ml-auto flex items-center gap-[1.2rem] font-semibold text-brand-secondary hover:scale-105 hover:text-brand-primary"
                href={nextLink.slug}
                scroll={true}
              >
                <p className="max-w-[20rem] text-right">{nextLink?.title}</p>
                <SafeImage
                  height={40}
                  width={40}
                  src={nextLink?.thumbnail}
                  alt={'next blog post image'}
                  className="h-[4rem] w-[4rem] grow rounded-[1rem] object-cover"
                />
                <ChevronRightIcon />
              </Link>
            </div>
          )}
        </div>
        <div className="relative hidden h-auto w-[43rem] lg:block">
          {ads?.map((ad, index) => (
            <Link
              key={`blog-ad-${index}`}
              href={ad.url}
              target="_blank"
              rel="sponsored nofollow"
              onClick={() => clickBlogAd(ad.id || 'unknown', 'vertical', ad.title, ad.url, ad.vendor || 'unknown', blog.slug)}
            >
              <div className="sticky right-0 top-[20rem] mb-[4rem] w-[22.8rem]">
                <SafeImage
                  width={228}
                  height={503}
                  src={ad.src}
                  alt={ad.title}
                />
                {ad.title && !ad.hideOverlay && (
                  <>
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[40%] bg-gradient-black" />
                    <div className="pointer-events-none absolute bottom-[1.2rem] left-[1.2rem] right-[1.2rem] text-[1.6rem] font-semibold leading-tight text-brand-white">
                      {ad.title}
                    </div>
                  </>
                )}
                <div className="text-[10px] text-gray-400 text-center mt-1">Affiliate link</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
};

BlogDetailPage.getLayout = function getLayout(page: React.ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export const getServerSideProps: GetServerSideProps<BlogDetailPageProps> = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, max-age=0, s-maxage=300, stale-while-revalidate=900'
  );
  
  const { slug: paramSlug } = context.params as Params;
  const slug = paramSlug?.join('/') ?? '';

  const blog = await getBlogBySlug(slug);

  if (!blog) {
    return {
      redirect: {
        destination: '/blog',
        permanent: false,
      },
    };
  }

  const ads = await getNAds(1, "vertical");

  // Add cache tags for on-demand invalidation
  addCacheTag(`blog-${slug}`);
  addCacheTag("blog");

  // Normalize the publish date (WP passes a date string despite the typed number)
  // and extract FAQ pairs from the post body for JSON-LD, both server-side.
  const datePublished = normalizeBlogDate(blog.created) ?? null;

  // WordPress `post_modified`, fetched separately from the persisted GraphQL query
  // (which does not select it), so it is frequently absent. resolveDateModified
  // owns the absent/out-of-order rules and is unit tested directly.
  const dateModified = resolveDateModified(
    datePublished,
    normalizeBlogDate(blog.modified) ?? null
  );

  const faqs = parseFaqFromHtml(blog.html);

  return {
    props: {
      blog: blog,
      ads,
      datePublished,
      dateModified,
      faqs,
    },
    //revalidate: 86400
  };
};

export default BlogDetailPage;

const CashIcon = () => {
  return (
    <svg
      width="80"
      height="80"
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.0254 31.9395H68.2754V64.8026H12.0254V31.9395Z"
        fill="#C75008"
      />
      <path
        opacity="0.3"
        d="M12.0254 31.9395H68.2754V64.8026H12.0254V31.9395Z"
        fill="white"
      />
      <path
        d="M63.2754 54.5704V42.1711C61.9006 42.1711 60.5821 41.6249 59.6099 40.6528C58.6378 39.6806 58.0916 38.3621 58.0916 36.9873H22.2091C22.2081 38.3618 21.6617 39.6797 20.6898 40.6517C19.7178 41.6236 18.3999 42.1701 17.0254 42.1711V54.5704C17.7061 54.5704 18.3802 54.7045 19.0091 54.965C19.638 55.2255 20.2095 55.6074 20.6909 56.0887C21.1722 56.5701 21.554 57.1415 21.8145 57.7704C22.0751 58.3994 22.2091 59.0734 22.2091 59.7542H58.0916C58.0916 58.3794 58.6378 57.0609 59.6099 56.0887C60.5821 55.1166 61.9006 54.5704 63.2754 54.5704Z"
        fill="#C75008"
      />
      <path
        d="M40.1504 54.9326C43.7748 54.9326 46.7129 51.9945 46.7129 48.3701C46.7129 44.7457 43.7748 41.8076 40.1504 41.8076C36.526 41.8076 33.5879 44.7457 33.5879 48.3701C33.5879 51.9945 36.526 54.9326 40.1504 54.9326Z"
        fill="white"
      />
      <path
        d="M66.8797 31.941H17.9609L60.2547 15.1973L66.8797 31.941Z"
        fill="#C75008"
      />
      <path
        opacity="0.3"
        d="M66.8797 31.941H17.9609L60.2547 15.1973L66.8797 31.941Z"
        fill="white"
      />
      <path
        d="M61.5051 31.9406H31.6738L52.6426 23.6406C52.8929 24.2735 53.2654 24.8508 53.7388 25.3396C54.2123 25.8285 54.7774 26.2193 55.4018 26.4898C56.0263 26.7602 56.698 26.905 57.3784 26.9159C58.0589 26.9268 58.7348 26.8035 59.3676 26.5531L61.5051 31.9406Z"
        fill="#C75008"
      />
    </svg>
  );
};
